import {
  IDocUnitLesson,
  IResourceParams,
  IUnitLessonParams,
  ParsedGoogleDocType,
} from './types'
import {
  ILessonPlan,
  ISimplifiedNode,
} from '../../components/ingestion/IngestedTree'
import {
  createAttachmentsList,
  createLessonPlan,
  createLongCardListType,
  createNoteNode,
} from '../nodeCreationHelpers'
import {
  flattenChildTablesWhereFirstCellIsHeader,
  getLessonElement,
  getLessonNum,
  lessonDescriptions,
  skillsListFromAlignments,
} from './utility'
import { parseLearningPlans } from './activity'
import { parseStudentInteractives } from './student_interactives'
import { parseStudentGuides } from './studentGuides'
import { createSnapshot } from './lessonSnapshot'
import {
  convertPhenomTypeToImgElement,
  getMetadataValueForKey,
  unStyledHtml,
} from './gDriveUtils'
import { additionalTeacherGuidance } from './additionalTeacherGuidance'
import { createMaterialPrep } from './lessonMaterialsPrep'
import { parseLessonItems } from './lessonActivities'
import { parseLessonAssessments } from './parseLessonAssessments'
import { wrapTag } from '../html_utility'

export function parseUnitLesson(
  metadata: IDocUnitLesson,
  lessonDoc: ParsedGoogleDocType,
  {
    gDrive,
    htmlContent,
    phenomIconMap,
    messageCallback,
  }: Pick<
    IResourceParams,
    'htmlContent' | 'gDrive' | 'phenomIconMap' | 'messageCallback'
  >,
): { lessonPlan: ILessonPlan; studentGuides: ISimplifiedNode[] } {
  const params: IUnitLessonParams = {
    metadata,
    lessonElement: getLessonElement(metadata, htmlContent),
    lessonDoc,
    gDrive,
    phenomIconMap,
    messageCallback,
  }
  const lessonPlan = createLessonPlan(
    {
      title: `Lesson ${getLessonNum(metadata)}: ${metadata.name}`,
      duration: {
        value: metadata.hasPart[metadata.hasPart.length - 1].day,
        unit: 'days',
      },
    },
    childNodes(params),
  )
  return {
    lessonPlan,
    studentGuides: parseStudentGuides(params),
  }
}

type addLessonChild = (params: IUnitLessonParams) => ISimplifiedNode[]

const childNodesParsers: addLessonChild[] = [
  getSkillsList,
  getLessonType,
  getLessonDescriptions,
  getWillFigureOut,
  createSnapshot,
  createMaterialPrep,
  getLessonPrintMaterials,
  additionalTeacherGuidance,
  getSupportingStudentsComponent,
  getLearningPlan,
  parseLessonItems,
  parseLessonAssessments,
  getStudentInteractives,
  getNextLessonDescription,
]

function childNodes(params: IUnitLessonParams) {
  return childNodesParsers.reduce((acc: ISimplifiedNode[], func) => {
    acc.push(...func(params))
    return acc
  }, [])
}

function getSkillsList(params: IUnitLessonParams) {
  const skillsList = skillsListFromAlignments(
    params.metadata.educationalAlignment,
  )
  if (skillsList) return [skillsList]
  return []
}

function getLessonType(params: IUnitLessonParams) {
  const imgElms = params.metadata.keywords
    .split(',')
    .reduce<string[]>((acc, kw) => {
      const imgElm = convertPhenomTypeToImgElement(
        kw.trim(),
        { style: 'height: 90px;' },
        params.phenomIconMap,
      )
      if (!imgElm) return acc
      return [...acc, wrapTag('div', imgElm, { style: 'width: 100px;' })]
    }, [])

  if (!imgElms.length) return []

  return [
    createNoteNode({
      title: params.metadata.learningResourceType,
      value: wrapTag('div', imgElms.join(''), { style: 'display: flex' }),
      studentFacing: false,
    }),
  ]
}

function getLessonDescriptions(params: IUnitLessonParams) {
  const metadata = params.metadata
  const present = metadata.description

  return lessonDescriptions({
    ...metadata,
    descriptionPresent: present,
  })
}

function getNextLessonDescription({
  metadata: { descriptionFuture },
}: IUnitLessonParams) {
  if (!descriptionFuture) return []
  return [
    createNoteNode({
      title: 'Next Lesson',
      value: descriptionFuture,
      studentFacing: false,
    }),
  ]
}

function getLearningPlan(params: IUnitLessonParams) {
  const nodes = parseLearningPlans({
    lessonTitle: params.metadata.name,
    lessonNum: getLessonNum(params.metadata),
    activities: params.metadata.hasPart,
    lessonElement: params.lessonElement,
  })
  const title = `Learning Plan`
  return [createLongCardListType({ title, nodes })]
}

function getStudentInteractives(params: IUnitLessonParams) {
  return parseStudentInteractives(params.lessonElement)
}

function getSupportingStudentsComponent({ lessonElement }: IUnitLessonParams) {
  const h1Elements = lessonElement.querySelectorAll('h1')

  const additionalLessonTeacherGuidance = Array.from(h1Elements || []).find(
    (h1) => {
      return /Additional Lesson \d+ Teacher Guidance/.test(
        h1.textContent?.trim() ?? '',
      )
    },
  )

  if (!additionalLessonTeacherGuidance) {
    return []
  }

  const tableElement = additionalLessonTeacherGuidance.nextElementSibling

  if (!tableElement) return []
  Array.from(tableElement.getElementsByTagName('a')).forEach((a) => {
    a.replaceWith(a.textContent ?? '')
  })
  const content = flattenChildTablesWhereFirstCellIsHeader({
    childNodes: [tableElement],
  } as any)

  return [
    createNoteNode({
      title: 'Supporting Students In Making Connections',
      value: content,
      studentFacing: false,
    }),
  ]
}

function getWillFigureOut(params: IUnitLessonParams) {
  const value = getMetadataValueForKey(
    'will-figure-out',
    params.lessonDoc.tableContent,
    unStyledHtml,
  )
  return [
    createNoteNode({
      title: 'What Students Will Figure Out',
      value,
      studentFacing: false,
    }),
  ]
}

function getLessonPrintMaterials() {
  return [
    createAttachmentsList({
      title: 'Lesson Print Materials',
      attachments_list: [],
    }),
  ]
}
